import {
  ApproveAndMapLetter,
  ApproveAndMapResult,
  ChangeLetterDesign,
  LetterCoverage,
  LetterTemplateResponse,
  RenameLetterTemplate,
  SendBackLetter,
  SetLetterCeo,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { request } from './client';

/**
 * Settings › Letters (APP-130 slice 1): the coverage grid, Approve and use, Send back and the
 * CEO / President line. The library's other calls stay in `letter-library.ts` and `settings.ts`.
 */
const templatePath = (id: string) =>
  `/api/v1/letter-templates/${encodeURIComponent(z.uuid().parse(id))}`;

/** Under `['letters']`, so every library change that refreshes the templates refreshes this. */
export const letterCoverageQuery = queryOptions({
  queryKey: ['letters', 'coverage'] as const,
  queryFn: ({ signal }) => request('/api/v1/letter-coverage', { schema: LetterCoverage, signal }),
  staleTime: 15_000,
});

/** Approves the sample the reviewer looked at and uses the version in its cell, in one step. */
export function approveAndMapLetter(id: string, body: ApproveAndMapLetter) {
  return request(`${templatePath(id)}/approve-and-map`, {
    method: 'POST',
    body: ApproveAndMapLetter.parse(body),
    schema: ApproveAndMapResult,
  });
}

/** Sends a draft back for a fix, with what needs fixing. */
export function sendBackLetter(id: string, body: SendBackLetter) {
  return request(`${templatePath(id)}/send-back`, {
    method: 'POST',
    body: SendBackLetter.parse(body),
    schema: LetterTemplateResponse,
  });
}

/** APP-130 D97: a letter's new name (any version that isn't retired), with the reason. */
export function renameLetterTemplate(id: string, body: RenameLetterTemplate) {
  return request(`${templatePath(id)}/rename`, {
    method: 'POST',
    body: RenameLetterTemplate.parse(body),
    schema: LetterTemplateResponse,
  });
}

/** APP-130 D97: a draft's new signer and/or mix, with the reason; its sample is made again. */
export function changeLetterDesign(id: string, body: ChangeLetterDesign) {
  return request(`${templatePath(id)}/design`, {
    method: 'POST',
    body: ChangeLetterDesign.parse(body),
    schema: LetterTemplateResponse,
  });
}

/** Changes who the CEO / President is for letters; answers with the grid as it now reads. */
export function setLetterCeo(body: SetLetterCeo) {
  return request('/api/v1/letter-library/ceo', {
    method: 'PUT',
    body: SetLetterCeo.parse(body),
    schema: LetterCoverage,
  });
}
